import axios from 'axios'
import router from '../router';
import { Message } from 'element-ui'
import i18n from '../i18n'
import { getRefreshToken, saveTokens } from './auth.js'
import messageCrypto from './messageCrypto.js'

const http = axios.create({
	baseURL: process.env.VUE_APP_BASE_API,
	timeout: 1000 * 30
})

// 需要对请求体里的内容字段做加密的接口：url 精确匹配 -> 请求体里要加密的字段路径
// webrtc/private 的 offer/answer/candidate 请求体本身就是一个裸字符串（无字段名，用 '' 表示整个 body）
const ENCRYPT_REQUEST_FIELDS = {	'/message/private/send': ['content'],
	'/message/group/send': ['content'],
	'/webrtc/private/offer': [''],
	'/webrtc/private/answer': [''],
	'/webrtc/private/candidate': [''],
	'/webrtc/group/offer': ['offer'],
	'/webrtc/group/answer': ['answer'],
	'/webrtc/group/candidate': ['candidate']
}

// 需要对响应体做解密的接口：url 精确匹配 -> 'object'(单条消息VO) | 'array'(消息VO数组)
const DECRYPT_RESPONSE_SHAPE = {
	'/message/private/send': 'object',
	'/message/group/send': 'object',
	'/message/private/history': 'array',
	'/message/group/history': 'array',
	'/message/private/loadOfflineMessage': 'array',
	'/message/group/loadOfflineMessage': 'array'
}

async function encryptRequestBody(url, data) {
	const fields = ENCRYPT_REQUEST_FIELDS[stripQuery(url)];
	if (!fields || data == null) {
		return data;
	}
	if (fields.length === 1 && fields[0] === '') {
		// 裸字符串请求体
		return await messageCrypto.encrypt(data);
	}
	for (const field of fields) {
		if (data[field] != null) {
			data[field] = await messageCrypto.encrypt(data[field]);
		}
	}
	return data;
}

async function decryptMessageVo(vo) {
	if (!vo) {
		return vo;
	}
	if (vo.content != null) {
		vo.content = await messageCrypto.decrypt(vo.content);
	}
	if (vo.quoteMessage && vo.quoteMessage.content != null) {
		vo.quoteMessage.content = await messageCrypto.decrypt(vo.quoteMessage.content);
	}
	return vo;
}

async function decryptResponseData(url, data) {
	const shape = DECRYPT_RESPONSE_SHAPE[stripQuery(url)];
	if (!shape || !messageCrypto.hasSessionKey() || data == null) {
		return data;
	}
	if (shape === 'array' && Array.isArray(data)) {
		await Promise.all(data.map(decryptMessageVo));
	} else if (shape === 'object') {
		await decryptMessageVo(data);
	}
	return data;
}

function stripQuery(url) {
	const idx = url.indexOf('?');
	return idx === -1 ? url : url.substring(0, idx);
}

/**
 * 请求拦截
 */
http.interceptors.request.use(async config => {
	const accessToken = sessionStorage.getItem("accessToken");
	if (accessToken) {
		config.headers.accessToken = encodeURIComponent(accessToken);
	}
	// 国际化：后端根据此 header 返回对应语言的消息
	const locale = i18n.locale;
	config.headers['Accept-Language'] = locale;
	config.headers['X-Locale'] = locale;
	// 已握手的会话对特定接口的内容字段加密；未握手/未启用时原样透传，兼容明文协议
	if (messageCrypto.hasSessionKey() && config.data != null) {
		config.data = await encryptRequestBody(config.url, config.data);
	}
	return config
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	if (response.data.code == 200) {
		const data = response.data.data;
		await decryptResponseData(response.config.url, data);
		return data;
	} else if (response.data.code == 400) {
		return exit();
	} else if (response.data.code == 401) {
		console.log("token失效，尝试重新获取")
		const refreshToken = getRefreshToken();
		if (!refreshToken) {
			return exit()
		}
		// 发送请求, 进行刷新token操作, 获取新的token
		const data = await http({
			method: 'put',
			url: '/refreshToken',
			headers: {
				refreshToken: refreshToken
			}
		}).catch(() => {
			exit();
			return Promise.reject(response.data);
		})
		saveTokens(data);
		response.config.headers.accessToken = encodeURIComponent(data.accessToken);
		// 重新发送刚才的请求
		return http(response.config)
	} else {
		Message({
			message: response.data.message,
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(response.data)
	}
}, error => {
	switch (error.response.status) {
		case 400:
			Message({
				message: error.response.data,
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 401:
			exit();
			break
		case 405:
			Message({
				message: i18n.t('http.methodError'),
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 404:
		case 500:
			Message({
				message: i18n.t('http.serverError'),
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 501:
			Message({
				message: i18n.t('http.notSupport'),
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
	}

	return Promise.reject(error)
})

const exit = () => {
	router.app.$eventBus.$emit("exit")
}


export default http
